import { haversineKm } from '@motogo/shared';
import type { Feature, FeatureCollection } from 'geojson';
import { useEffect, useMemo, useRef, useState } from 'react';

import { bearingDegrees } from '@/lib/routing';
import type { GpsPosition } from '@/types/geo';

/**
 * Tracks where the rider is *along* the loaded route — the one number (distance ridden,
 * s) that everything route-aware hangs off: the anchored line emphasis, the progress bar,
 * remaining distance and ETA, and eventually off-route rerouting (nav v2).
 *
 * Matching to a known route is deliberately much simpler than general map-matching:
 * while locked, the rider's position is projected onto the polyline only within a window
 * around the current progress — the monotonic-progress assumption is exactly what
 * disambiguates *which pass* of a road the route uses twice. Lock-on (and re-lock after
 * going off-route) is a global nearest-point search gated by heading agreement when
 * moving; when stationary it locks only if the match is unambiguous (no second candidate
 * pass nearby). Losing the route for a few consecutive fixes unlocks.
 *
 * Pure client-side per GPS tick over at most ~10k vertices — no backend, no PostGIS.
 */

export type RouteProgressStatus = 'idle' | 'searching' | 'onRoute' | 'offRoute';

export interface RouteProgress {
  status: RouteProgressStatus;
  /** Meters ridden along the route. Only meaningful while status is 'onRoute'. */
  sMeters: number;
  totalMeters: number;
  remainingMeters: number;
  /** 0..1 of the route completed. */
  fraction: number;
  /** Seconds to the finish from recent moving speed; null until there's a speed sample. */
  etaSeconds: number | null;
}

const IDLE: RouteProgress = {
  status: 'idle',
  sMeters: 0,
  totalMeters: 0,
  remainingMeters: 0,
  fraction: 0,
  etaSeconds: null,
};

// Matching tunables (meters / degrees / ticks). GPS ticks arrive ~1/s.
const LOCK_M = 60; // must be this close to the line to lock on
const OFFROUTE_M = 75; // farther than this (while locked) counts as a miss
const OFFROUTE_TICKS = 4; // consecutive misses before unlocking
const WINDOW_BACK_M = 200; // how far progress may slip backwards while locked
const WINDOW_FWD_M = 800; // how far a single tick may advance (≈ 100 s at 30 km/h gaps)
const HEADING_TOL_DEG = 70; // GPS heading vs route bearing agreement for lock-on
const AMBIGUOUS_GAP_M = 300; // two lock candidates farther apart along-route than this = ambiguous
const MOVING_MPS = 1.4; // ~5 km/h — below this GPS heading is noise

// ETA speed estimate: exponential moving average over moving samples only. At ~1 tick/s,
// alpha 0.05 ≈ a half-minute memory — long enough to flatten corners, short enough to
// react to leaving the twisties for a straight.
const EMA_ALPHA = 0.05;
const MIN_ETA_SPEED_MPS = 1.4;

/** All LineString coordinates in the route FeatureCollection, concatenated in order. */
export function extractRouteLine(route: FeatureCollection): [number, number][] {
  const coords: [number, number][] = [];
  for (const feature of route.features) {
    if (feature.geometry.type === 'LineString') {
      coords.push(...(feature.geometry.coordinates as [number, number][]));
    }
  }
  return coords;
}

/** Cumulative meters at each vertex (index 0 = 0). */
export function cumulativeMeters(coords: [number, number][]): number[] {
  const cum = new Array<number>(coords.length);
  cum[0] = 0;
  for (let i = 1; i < coords.length; i++) {
    cum[i] = cum[i - 1] + haversineKm(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0]) * 1000;
  }
  return cum;
}

/**
 * The route pre-cut into ~chunkMeters LineString features, each carrying its midpoint
 * cumulative distance `s` — the static data the anchored-emphasis map layers filter and
 * colour against (a per-feature stand-in for line-gradient, which can't span features).
 */
export function buildProgressChunks(coords: [number, number][], cum: number[], chunkMeters = 150): Feature[] {
  const features: Feature[] = [];
  let start = 0;
  for (let i = 1; i < coords.length; i++) {
    if (cum[i] - cum[start] >= chunkMeters || i === coords.length - 1) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords.slice(start, i + 1) },
        properties: { s: (cum[start] + cum[i]) / 2 },
      });
      start = i;
    }
  }
  return features;
}

interface Candidate {
  /** Distance from the fix to the line, meters. */
  distM: number;
  /** Progress along the route at the projected point, meters. */
  s: number;
  /** Route bearing at the matched segment, compass degrees. */
  bearing: number;
}

function headingAgrees(heading: number, bearing: number): boolean {
  const diff = Math.abs(heading - bearing) % 360;
  return Math.min(diff, 360 - diff) <= HEADING_TOL_DEG;
}

/**
 * Nearest point on the polyline to the fix, scanning segments [from, to). Works in a
 * local equirectangular frame centred on the fix — centimetre-accurate at these scales.
 */
function nearestOnLine(
  coords: [number, number][],
  cum: number[],
  fix: { lat: number; lon: number },
  from: number,
  to: number,
): Candidate | null {
  const mPerDegLat = 110_540;
  const mPerDegLon = 111_320 * Math.cos((fix.lat * Math.PI) / 180);
  let best: Candidate | null = null;

  let ax = (coords[from][0] - fix.lon) * mPerDegLon;
  let ay = (coords[from][1] - fix.lat) * mPerDegLat;
  for (let i = from; i < to; i++) {
    const bx = (coords[i + 1][0] - fix.lon) * mPerDegLon;
    const by = (coords[i + 1][1] - fix.lat) * mPerDegLat;
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    // The fix is the origin, so projecting it onto AB is just -A·AB / |AB|².
    const t = len2 > 0 ? Math.min(1, Math.max(0, -(ax * dx + ay * dy) / len2)) : 0;
    const px = ax + t * dx;
    const py = ay + t * dy;
    const distM = Math.hypot(px, py);
    if (!best || distM < best.distM) {
      best = {
        distM,
        s: cum[i] + t * (cum[i + 1] - cum[i]),
        bearing: bearingDegrees(
          { lat: coords[i][1], lon: coords[i][0] },
          { lat: coords[i + 1][1], lon: coords[i + 1][0] },
        ),
      };
    }
    ax = bx;
    ay = by;
  }
  return best;
}

/** First vertex index whose cumulative distance is ≥ s (binary search). */
function vertexAt(cum: number[], s: number): number {
  let lo = 0;
  let hi = cum.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (cum[mid] < s) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Global lock-on scan. Returns the candidate to lock to, or null: when moving, the best
 * heading-agreeing candidate; when stationary, the best candidate only if no second
 * candidate sits on a different pass of the route (ambiguity is deferred to movement).
 */
function scanForLock(
  coords: [number, number][],
  cum: number[],
  position: GpsPosition,
): Candidate | null {
  const moving =
    position.speed != null && !Number.isNaN(position.speed) && position.speed > MOVING_MPS;
  const heading = position.heading != null && !Number.isNaN(position.heading) ? position.heading : null;

  // One pass over all segments, tracking the best overall and the best heading-agreeing
  // candidate, plus whether a second in-range candidate exists on a distant part of the
  // route (local minima more than AMBIGUOUS_GAP_M apart along s).
  const mPerDegLat = 110_540;
  const mPerDegLon = 111_320 * Math.cos((position.lat * Math.PI) / 180);
  let best: Candidate | null = null;
  let bestAgreeing: Candidate | null = null;
  let ambiguousWith: number | null = null; // s of some other in-range stretch

  let ax = (coords[0][0] - position.lon) * mPerDegLon;
  let ay = (coords[0][1] - position.lat) * mPerDegLat;
  for (let i = 0; i < coords.length - 1; i++) {
    const bx = (coords[i + 1][0] - position.lon) * mPerDegLon;
    const by = (coords[i + 1][1] - position.lat) * mPerDegLat;
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.min(1, Math.max(0, -(ax * dx + ay * dy) / len2)) : 0;
    const distM = Math.hypot(ax + t * dx, ay + t * dy);
    ax = bx;
    ay = by;
    if (distM > LOCK_M) continue;

    const s = cum[i] + t * (cum[i + 1] - cum[i]);
    const candidate: Candidate = {
      distM,
      s,
      bearing: bearingDegrees(
        { lat: coords[i][1], lon: coords[i][0] },
        { lat: coords[i + 1][1], lon: coords[i + 1][0] },
      ),
    };
    if (!best || distM < best.distM) {
      if (best && Math.abs(best.s - s) > AMBIGUOUS_GAP_M) ambiguousWith = best.s;
      best = candidate;
    } else if (Math.abs(best.s - s) > AMBIGUOUS_GAP_M) {
      ambiguousWith = s;
    }
    if (heading != null && moving && headingAgrees(heading, candidate.bearing)) {
      if (!bestAgreeing || distM < bestAgreeing.distM) bestAgreeing = candidate;
    }
  }

  if (moving && heading != null) return bestAgreeing; // heading resolves doubled roads
  if (!best) return null;
  return ambiguousWith === null ? best : null; // stationary on an overlap: wait for movement
}

export function useRouteProgress(
  route: FeatureCollection | null,
  position: GpsPosition | null,
): RouteProgress {
  const geometry = useMemo(() => {
    if (!route) return null;
    const coords = extractRouteLine(route);
    if (coords.length < 2) return null;
    const cum = cumulativeMeters(coords);
    return { coords, cum, total: cum[cum.length - 1] };
  }, [route]);

  const [progress, setProgress] = useState<RouteProgress>(IDLE);
  // Lock state lives in refs — per-tick bookkeeping, only the public snapshot is state.
  const lockRef = useRef<{ s: number } | null>(null);
  const missCountRef = useRef(0);
  const wasLockedRef = useRef(false);
  const emaSpeedRef = useRef<number | null>(null);

  // A new (or cleared) route starts the matching from scratch.
  useEffect(() => {
    lockRef.current = null;
    missCountRef.current = 0;
    wasLockedRef.current = false;
    setProgress(geometry ? { ...IDLE, status: 'searching', totalMeters: geometry.total } : IDLE);
  }, [geometry]);

  useEffect(() => {
    if (!geometry || !position) return;
    const { coords, cum, total } = geometry;

    // Speed EMA (moving samples only) — feeds the ETA regardless of lock state.
    if (position.speed != null && !Number.isNaN(position.speed) && position.speed > MIN_ETA_SPEED_MPS) {
      const ema = emaSpeedRef.current;
      emaSpeedRef.current = ema === null ? position.speed : ema + EMA_ALPHA * (position.speed - ema);
    }

    let lock = lockRef.current;
    if (lock) {
      // Locked: project only within the progress window — this is what picks the right
      // pass of a doubled road, and what keeps the cost trivial.
      const fromIdx = Math.max(0, vertexAt(cum, lock.s - WINDOW_BACK_M) - 1);
      const toIdx = Math.min(coords.length - 1, vertexAt(cum, lock.s + WINDOW_FWD_M));
      const hit = fromIdx < toIdx ? nearestOnLine(coords, cum, position, fromIdx, toIdx) : null;
      if (hit && hit.distM <= OFFROUTE_M) {
        missCountRef.current = 0;
        lock = { s: hit.s };
      } else {
        missCountRef.current += 1;
        if (missCountRef.current >= OFFROUTE_TICKS) {
          lock = null;
          missCountRef.current = 0;
        }
      }
    } else {
      const hit = scanForLock(coords, cum, position);
      if (hit) {
        lock = { s: hit.s };
        missCountRef.current = 0;
      }
    }

    lockRef.current = lock;
    if (lock) wasLockedRef.current = true;

    const s = lock ? lock.s : 0;
    const remaining = lock ? Math.max(0, total - s) : total;
    const ema = emaSpeedRef.current;
    setProgress({
      status: lock ? 'onRoute' : wasLockedRef.current ? 'offRoute' : 'searching',
      sMeters: s,
      totalMeters: total,
      remainingMeters: remaining,
      fraction: lock && total > 0 ? s / total : 0,
      etaSeconds: lock && ema !== null && ema > MIN_ETA_SPEED_MPS ? remaining / ema : null,
    });
    // Ticks are driven by the fix; geometry resets handled above.
  }, [position, geometry]);

  return progress;
}
